import React, { useEffect, useRef, useState } from 'react';
import { useUpdateMode } from '../../hooks/useUpdateMode';
import useDraggableTab from '../../hooks/useDraggableTab';

const INTRO_KEY = 'myl_pet_tab_intro_done';
const INTRO_DURATION_MS = 1800;

interface PetFabTabProps {
  petCount: number;
  currentStep: number;
  onOpen: () => void;
}

const PetFabTab: React.FC<PetFabTabProps> = ({ petCount, currentStep, onOpen }) => {
  const { isUpdateMode } = useUpdateMode();
  const [containerHeight, setContainerHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 800);
  const [isExpanded, setIsExpanded] = useState(false);
  const introDoneRef = useRef(
    (() => {
      try {
        return sessionStorage.getItem(INTRO_KEY) === 'true';
      } catch {
        return false;
      }
    })(),
  );

  const { tabY, isDragging, bindPointer } = useDraggableTab(containerHeight, onOpen);

  useEffect(() => {
    const onResize = () => setContainerHeight(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Intro animation: expand once on first mount, then collapse
  useEffect(() => {
    if (petCount === 0 || isUpdateMode || currentStep === 14) return;
    if (introDoneRef.current) return;
    setIsExpanded(true);
    const timer = setTimeout(() => {
      setIsExpanded(false);
      introDoneRef.current = true;
      try {
        sessionStorage.setItem(INTRO_KEY, 'true');
      } catch {}
    }, INTRO_DURATION_MS);
    return () => clearTimeout(timer);
  }, [petCount, isUpdateMode, currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  if (petCount === 0 || isUpdateMode || currentStep === 14) return null;

  const showExpanded = isExpanded || isDragging;

  return (
    <div
      className="lg:hidden fixed right-0 z-40 select-none"
      style={{ top: `${tabY}px`, touchAction: 'none' }}
      data-testid="pet-fab-tab"
    >
      <button
        type="button"
        aria-label="Abrir mis mascotas"
        {...bindPointer}
        className={`bg-[#008BBC] text-white rounded-l-xl shadow-lg overflow-hidden flex items-center transition-all duration-300 ${
          showExpanded ? 'w-[90px] px-2' : 'w-[18px]'
        }`}
        style={{ minHeight: '52px' }}
      >
        {showExpanded ? (
          <div className="flex items-center gap-1.5 w-full py-3">
            <span className="text-base leading-none shrink-0">🐕</span>
            <div>
              <div className="text-[11px] font-bold leading-tight whitespace-nowrap">Mis mascotas</div>
              <div className="text-[9px] opacity-80 leading-tight whitespace-nowrap">mantén para mover</div>
            </div>
            <span className="text-[9px] font-bold leading-none ml-auto" data-testid="pet-count-badge">
              {petCount}
            </span>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center w-full py-3">
            <span className="text-xs leading-none">🐕</span>
            <span className="text-[9px] font-bold leading-none mt-0.5" data-testid="pet-count-badge">
              {petCount}
            </span>
          </div>
        )}
      </button>
    </div>
  );
};

export default PetFabTab;
